import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  getDocs,
  updateDoc,
  query,
  where,
  serverTimestamp,
  writeBatch,
  GeoPoint,
} from "firebase/firestore";
import { db } from "@/config/firebase";
import { COLLECTIONS } from "@/config/constants";
import type { OsmRestaurant } from "@/types/osm.types";
import ngeohash from "ngeohash";

function normalizeOsmId(raw: string | number): string {
  const value = String(raw).trim();
  if (!value) return value;

  const lower = value.toLowerCase();
  if (lower.startsWith("osm_way_")) return `way/${value.slice(8)}`;
  if (lower.startsWith("osm_relation_")) return `relation/${value.slice(13)}`;
  if (lower.startsWith("osm_node_")) return value.slice(9);
  if (lower.startsWith("node/")) return value.slice(5);
  return value;
}

function firestoreBarIdFromOsmId(raw: string | number): string {
  const normalized = normalizeOsmId(raw);
  if (!normalized.includes("/")) return normalized;

  const slashIndex = normalized.indexOf("/");
  const type = normalized.slice(0, slashIndex).trim().toLowerCase();
  const bareId = normalized.slice(slashIndex + 1).trim();

  if (!bareId) return normalized.split("/").join("_");
  if (type === "node") return bareId;
  if (type === "way" || type === "relation") return `osm_${type}_${bareId}`;
  return `osm_${type.replace(/[^a-z0-9]+/g, "_")}_${bareId}`;
}

const GEOHASH_PRECISION = 7;

const AMENITY_LABELS: Record<string, string> = {
  restaurant: "Restaurant",
  cafe: "Café",
  pub: "Pub",
  bar: "Bar",
  fast_food: "Fast Food",
  biergarten: "Biergarten",
  food_court: "Food Court",
  ice_cream: "Eisdiele",
  nightclub: "Club",
};

/** Maps OSM amenity types to the BarType enum used in the Flutter app */
const OSM_TO_BAR_TYPE: Record<string, string> = {
  bar: "bar",
  pub: "pub",
  nightclub: "club",
  biergarten: "biergarten",
  restaurant: "bar",
  cafe: "bar",
  fast_food: "bar",
  food_court: "bar",
  ice_cream: "bar",
};

/**
 * Erstellt das vollständige Firestore-Dokument für eine Bar,
 * kompatibel mit dem Flutter-App Bar-Model (bar.dart fromFirestore).
 */
function buildBarDocument(r: OsmRestaurant, adminUid: string) {
  const tags = r.tags;
  const geohash = ngeohash.encode(r.lat, r.lon, GEOHASH_PRECISION);

  return {
    // Pflichtfelder für die Flutter-App Map-Anzeige
    name: r.name,
    latitude: r.lat,
    longitude: r.lon,
    location: new GeoPoint(r.lat, r.lon),
    geohash,

    // Adresse als verschachteltes Objekt (Flutter-App erwartet addressDetails)
    addressDetails: {
      street: tags["addr:street"] || "",
      housenumber: tags["addr:housenumber"] || "",
      city: tags["addr:city"] || tags["addr:suburb"] || "",
      postcode: tags["addr:postcode"] || "",
      country: tags["addr:country"] || "DE",
    },

    // BarType (Flutter BarType.fromString)
    type: OSM_TO_BAR_TYPE[r.amenity] || "bar",

    // OSM-Herkunft
    source: "osm",
    osmId: String(r.id),
    osmName: r.name,
    osmAmenity: r.amenity,
    osmAmenityLabel: AMENITY_LABELS[r.amenity] || r.amenity,

    description: tags.description || "",
    cuisine: r.cuisine.join(", ") || "",
    importedFromOsm: true,

    // Status & Sichtbarkeit
    isClaimed: false,
    isVerified: false,
    status: "active",
    visibility: "public",

    // Tags für Filterung
    tags: [r.amenity, ...r.cuisine].filter(Boolean),

    // Amenities (Equipment/Features)
    amenities: [
      ...(tags.outdoor_seating === "yes" ? ["outdoor_seating"] : []),
      ...(tags.wheelchair === "yes" ? ["wheelchair"] : []),
      ...(tags.internet_access === "wlan" ? ["wifi"] : []),
      ...(tags.takeaway === "yes" ? ["takeaway"] : []),
      ...(tags.delivery === "yes" ? ["delivery"] : []),
    ],

    // Contact
    contact: {
      ...(tags.phone && { phone: tags.phone }),
      ...(tags.email && { email: tags.email }),
      ...(tags.website && { website: tags.website }),
    },

    // Social Media
    socials: {
      ...(tags["contact:instagram"] && { instagram: tags["contact:instagram"] }),
      ...(tags["contact:facebook"] && { facebook: tags["contact:facebook"] }),
    },

    // Opening Hours (Rohformat aus OSM)
    ...(tags.opening_hours && { openingHoursRaw: tags.opening_hours }),

    // Statistiken (initialisiert)
    stats: {
      viewCount: 0,
      favoriteCount: 0,
      checkInCount: 0,
      contributionCount: 0,
    },
    favoritesCount: 0,

    // Bilder (leer initialisiert)
    images: {
      cover: null,
      gallery: [],
    },

    // Timestamps
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    importedAt: serverTimestamp(),
    importedBy: adminUid,
    createdBy: adminUid,
  };
}

/**
 * Service zum Importieren, Archivieren und Verwalten von Bars aus OSM-Daten.
 */
export const osmBarsService = {
  /**
   * Importiert einen einzelnen OSM-Ort als Bar in Firestore.
   */
  importBar: async (restaurant: OsmRestaurant, adminUid: string): Promise<string> => {
    try {
      const barId = firestoreBarIdFromOsmId(restaurant.id);
      const barRef = doc(db, COLLECTIONS.BARS, barId);
      await setDoc(barRef, buildBarDocument(restaurant, adminUid));
      return barId;
    } catch (error) {
      console.error("Error importing bar from OSM:", error);
      throw new Error(`Fehler beim Importieren von "${restaurant.name}"`);
    }
  },

  /**
   * Importiert mehrere OSM-Orte als Batch.
   */
  importBars: async (restaurants: OsmRestaurant[], adminUid: string): Promise<{ imported: number; errors: string[] }> => {
    const errors: string[] = [];
    let imported = 0;

    const chunks: OsmRestaurant[][] = [];
    for (let i = 0; i < restaurants.length; i += 400) {
      chunks.push(restaurants.slice(i, i + 400));
    }

    for (const chunk of chunks) {
      const batch = writeBatch(db);
      for (const r of chunk) {
        try {
          const barId = firestoreBarIdFromOsmId(r.id);
          const barRef = doc(db, COLLECTIONS.BARS, barId);
          batch.set(barRef, buildBarDocument(r, adminUid));
          imported++;
        } catch (err) {
          errors.push(`${r.name}: ${err instanceof Error ? err.message : "Unbekannter Fehler"}`);
        }
      }
      try {
        await batch.commit();
      } catch (err) {
        errors.push(`Batch-Fehler: ${err instanceof Error ? err.message : "Unbekannt"}`);
      }
    }

    return { imported, errors };
  },

  /**
   * Archiviert eine Bar (Soft-Delete) — Bar wird in der App nicht mehr angezeigt.
   */
  archiveBar: async (osmId: string): Promise<void> => {
    try {
      const barRef = doc(db, COLLECTIONS.BARS, firestoreBarIdFromOsmId(osmId));
      await updateDoc(barRef, {
        status: "archived",
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Error archiving bar:", error);
      throw new Error("Fehler beim Archivieren der Bar");
    }
  },

  /**
   * Reaktiviert eine archivierte Bar.
   */
  unarchiveBar: async (osmId: string): Promise<void> => {
    try {
      const barRef = doc(db, COLLECTIONS.BARS, firestoreBarIdFromOsmId(osmId));
      await updateDoc(barRef, {
        status: "active",
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Error unarchiving bar:", error);
      throw new Error("Fehler beim Reaktivieren der Bar");
    }
  },

  /**
   * Batch-Archivierung mehrerer Bars.
   */
  archiveBars: async (osmIds: string[]): Promise<{ archived: number; errors: string[] }> => {
    const errors: string[] = [];
    let archived = 0;

    const chunks: string[][] = [];
    for (let i = 0; i < osmIds.length; i += 400) {
      chunks.push(osmIds.slice(i, i + 400));
    }

    for (const chunk of chunks) {
      const batch = writeBatch(db);
      for (const id of chunk) {
        const barRef = doc(db, COLLECTIONS.BARS, firestoreBarIdFromOsmId(id));
        batch.update(barRef, { status: "archived", updatedAt: serverTimestamp() });
        archived++;
      }
      try {
        await batch.commit();
      } catch (err) {
        errors.push(`Batch-Fehler: ${err instanceof Error ? err.message : "Unbekannt"}`);
      }
    }

    return { archived, errors };
  },

  /**
   * Hard-Delete einer Bar aus Firestore.
   */
  removeBar: async (osmId: string): Promise<void> => {
    try {
      const barRef = doc(db, COLLECTIONS.BARS, firestoreBarIdFromOsmId(osmId));
      await deleteDoc(barRef);
    } catch (error) {
      console.error("Error removing bar:", error);
      throw new Error("Fehler beim Entfernen der Bar");
    }
  },

  /**
   * Entfernt mehrere Bars als Batch.
   */
  removeBars: async (osmIds: string[]): Promise<{ removed: number; errors: string[] }> => {
    const errors: string[] = [];
    let removed = 0;

    const chunks: string[][] = [];
    for (let i = 0; i < osmIds.length; i += 400) {
      chunks.push(osmIds.slice(i, i + 400));
    }

    for (const chunk of chunks) {
      const batch = writeBatch(db);
      for (const id of chunk) {
        const barRef = doc(db, COLLECTIONS.BARS, firestoreBarIdFromOsmId(id));
        batch.delete(barRef);
        removed++;
      }
      try {
        await batch.commit();
      } catch (err) {
        errors.push(`Batch-Fehler: ${err instanceof Error ? err.message : "Unbekannt"}`);
      }
    }

    return { removed, errors };
  },

  /**
   * Lädt alle OSM-IDs von bereits importierten Bars inkl. Status.
   */
  getExistingOsmBars: async (): Promise<Map<string, { status: string }>> => {
    try {
      const barsRef = collection(db, COLLECTIONS.BARS);
      const q = query(barsRef, where("importedFromOsm", "==", true));
      const snapshot = await getDocs(q);

      const bars = new Map<string, { status: string }>();
      snapshot.forEach((d) => {
        const data = d.data();
        if (data.osmId) {
          bars.set(normalizeOsmId(data.osmId), { status: data.status || "active" });
        }
      });
      return bars;
    } catch (error) {
      console.error("Error fetching existing OSM bars:", error);
      return new Map();
    }
  },

  // ─── Blacklist für reine OSM-Punkte (nicht in Firestore importiert) ───

  /**
   * Setzt einen OSM-Punkt auf die Blacklist — wird in der App nicht mehr als OSM-Marker angezeigt.
   */
  blacklistOsmPoint: async (osmId: string, reason: string, adminUid: string): Promise<void> => {
    try {
      const normalizedOsmId = normalizeOsmId(osmId);
      const blRef = doc(db, COLLECTIONS.BARS_BLACKLIST, firestoreBarIdFromOsmId(normalizedOsmId));
      await setDoc(blRef, {
        osmId: normalizedOsmId,
        reason,
        blacklistedAt: serverTimestamp(),
        blacklistedBy: adminUid,
      });
    } catch (error) {
      console.error("Error blacklisting OSM point:", error);
      throw new Error("Fehler beim Blacklisten des OSM-Punktes");
    }
  },

  /**
   * Entfernt einen OSM-Punkt von der Blacklist.
   */
  unblacklistOsmPoint: async (osmId: string): Promise<void> => {
    try {
      const blRef = doc(db, COLLECTIONS.BARS_BLACKLIST, firestoreBarIdFromOsmId(osmId));
      await deleteDoc(blRef);
    } catch (error) {
      console.error("Error unblacklisting OSM point:", error);
      throw new Error("Fehler beim Entfernen von der Blacklist");
    }
  },

  /**
   * Lädt alle geblacklisteten OSM-IDs.
   */
  getBlacklistedOsmIds: async (): Promise<Set<string>> => {
    try {
      const blRef = collection(db, COLLECTIONS.BARS_BLACKLIST);
      const snapshot = await getDocs(blRef);

      const ids = new Set<string>();
      snapshot.forEach((d) => {
        const data = d.data();
        if (data.osmId) {
          ids.add(normalizeOsmId(data.osmId));
        }
      });
      return ids;
    } catch (error) {
      console.error("Error fetching blacklisted OSM IDs:", error);
      return new Set();
    }
  },
};
